// Considered: WizardStepsAtom — rejected because it drives a single `activeIndex` and cannot
// express several concurrently running steps, per-step status/duration/error, nested child runs,
// or a terminal summary. Considered: ArcHistoryTimelineMolecule / CompactTimelineMolecule —
// rejected because both are bound to a sphere + arc template / timeline API and render past
// events, not a live DAG. See specs/saga-progress.md Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type {
  ProgressStep,
  ProgressStepLabel,
  SagaProgress,
  SagaProgressEstimate,
  EmbeddedSagaProgress,
} from 'types/sagaProgress';
import StatusBadgeAtom, { StatusBadgeTone } from '../../atoms/StatusBadge/StatusBadgeAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ProgressBarAtom from '../../atoms/ProgressBar/ProgressBarAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import { formatShortDuration } from '../../../utils/formatShortDuration';
import { useSagaProgressAnalytics, type SagaProgressSurface } from './useSagaProgressAnalytics';
import './SagaProgressMolecule.scss';

export interface SagaProgressMoleculeProps {
  /**
   * Current snapshot from `useSagaProgress`, or the synthetic "preparing"
   * stand-in `provisioning-status` embeds before a run exists. `null` before
   * the first response.
   */
  progress: EmbeddedSagaProgress | null;
  /** True while a fetch is in flight and no snapshot has arrived yet. */
  loading: boolean;
  /** Non-null while consecutive fetch failures (other than 403/404) are ongoing. */
  error: Error | null;
  /** From `useSagaProgress().deniedReason` — set once a 403/404 permanently stops polling. */
  deniedReason?: 'forbidden' | 'not_found' | null;
  /** Wired to `useSagaProgress().refetch` — omit to hide the retry action. */
  onRetry?: () => void;
  /**
   * `block` (default): full-width standalone surface — the sign-up provisioning screen.
   * `inline`: embedded in a dialog body (deploy-approach post-deploy step) — tighter spacing,
   * and the failure state names the Approaches tab as the way to deploy again.
   */
  variant?: 'block' | 'inline';
  /** Where this view is mounted — carried on every saga-progress analytics event. */
  surface: SagaProgressSurface;
  className?: string;
}

type Translate = (key: string, opts?: Record<string, string | number>) => string;

type BadgeStatus = 'completed' | 'running' | 'waiting' | 'pending' | 'failed' | 'reverted';

const STATUS_TONE: Record<BadgeStatus, StatusBadgeTone> = {
  completed: 'success',
  running: 'info',
  waiting: 'neutral',
  pending: 'neutral',
  failed: 'danger',
  reverted: 'warn',
};

const STATUS_WORD_KEY: Record<BadgeStatus, string> = {
  completed: 'sagaProgress.step.status.completed',
  running: 'sagaProgress.step.status.running',
  waiting: 'sagaProgress.step.status.waiting',
  pending: 'sagaProgress.step.status.pending',
  failed: 'sagaProgress.step.status.failed',
  reverted: 'sagaProgress.step.status.reverted',
};

/**
 * A glyph per status as well as the word, so the three terminal row states stay distinguishable
 * without relying on tone (ux-craft A6). No spinner: an infinite rotation on every running row
 * is motion nothing can turn off.
 */
const STATUS_ICON: Record<BadgeStatus, string> = {
  completed: 'pi pi-check',
  running: 'pi pi-circle-fill',
  waiting: 'pi pi-clock',
  pending: 'pi pi-circle',
  failed: 'pi pi-times',
  reverted: 'pi pi-undo',
};

/** `compensated` is the wire word for a step whose work was rolled back; `reverted` is the user's. */
function badgeStatusOf(step: ProgressStep): BadgeStatus {
  return step.status === 'compensated' ? 'reverted' : step.status;
}

/** Resolves a step's i18n key + params (which may carry `LocalizedString` values) to display text. */
function resolveStepLabel(label: ProgressStepLabel, t: Translate): string {
  if (!label.params) return t(label.key);
  const resolved: Record<string, string> = {};
  for (const [key, value] of Object.entries(label.params)) {
    // `getLocalizedText` resolves against the reader's current locale, so a French reader gets
    // the French template name inside the French sentence rather than the `default`.
    resolved[key] = getLocalizedText(value);
  }
  return t(label.key, resolved);
}

function estimateText(estimate: SagaProgressEstimate, t: Translate): string | null {
  if (estimate.confidence === 'none') return null;
  if (estimate.confidence === 'low') return t('sagaProgress.estimate.low');
  if (estimate.remainingMs == null) return null;
  return t('sagaProgress.estimate.high', { duration: formatShortDuration(estimate.remainingMs, t) });
}

/** The subject the run is acting on — the approach being deployed, else the target sphere. */
function subjectHeading(progress: SagaProgress, t: Translate): string {
  const approachName = getLocalizedText(progress.subject.approachName);
  if (approachName) return t('sagaProgress.heading.approach', { name: approachName });
  const sphereName = getLocalizedText(progress.subject.targetSphereName);
  if (sphereName) return t('sagaProgress.heading.sphere', { name: sphereName });
  return t('sagaProgress.heading.generic');
}

interface StepStatusBadgeProps {
  status: BadgeStatus;
  t: Translate;
}

const StepStatusBadge: React.FC<StepStatusBadgeProps> = ({ status, t }) => (
  <StatusBadgeAtom
    tone={STATUS_TONE[status]}
    icon={STATUS_ICON[status]}
    label={t(STATUS_WORD_KEY[status])}
    size="small"
  />
);

interface StepRowProps {
  step: ProgressStep;
  status: BadgeStatus;
  t: Translate;
}

const StepRow: React.FC<StepRowProps> = ({ step, status, t }) => (
  <div
    className={`saga-progress__row saga-progress__row--${status}`}
    style={{ '--saga-progress-row-depth': step.depth } as React.CSSProperties}
    data-testid={`saga-progress-row-${step.id}`}
  >
    <StepStatusBadge status={status} t={t} />
    <span className="saga-progress__row-label">{resolveStepLabel(step.label, t)}</span>
    {step.durationMs != null && (
      <span className="saga-progress__row-duration">{formatShortDuration(step.durationMs, t)}</span>
    )}
    {step.error && <span className="saga-progress__error-message">{t(step.error.messageKey)}</span>}
  </div>
);

interface StepRowsProps {
  rows: { step: ProgressStep; status: BadgeStatus }[];
  t: Translate;
}

/**
 * The one scrolling container in the molecule. A named `<section>` with `tabIndex={0}`, so a
 * keyboard-only reader can reach and scroll a 40–60 step foundation list (ux-craft A1).
 */
const StepRows: React.FC<StepRowsProps> = ({ rows, t }) => (
  <section
    className="saga-progress__rows"
    aria-label={t('sagaProgress.steps.regionLabel')}
    tabIndex={0}
    data-testid="saga-progress-rows"
  >
    {rows.map(({ step, status }) => (
      <StepRow key={step.id} step={step} status={status} t={t} />
    ))}
  </section>
);

interface ActivityLineProps {
  progress: SagaProgress;
  t: Translate;
}

/**
 * The live region is mounted for the whole life of the working view and only its text changes —
 * a region that appears together with its first message is commonly not announced at all.
 */
const ActivityLine: React.FC<ActivityLineProps> = ({ progress, t }) => {
  const running = progress.currentActivity;
  const shown = running.slice(0, 3);
  const remaining = running.length - shown.length;
  const retryingStep = shown.find((step) => step.retryCount > 0);

  let body: React.ReactNode;
  if (shown.length === 0) {
    body = <span>{progress.status === 'waiting' ? t('sagaProgress.waiting.generic') : t('sagaProgress.activity.idle')}</span>;
  } else {
    body = (
      <>
        <span>{shown.map((step) => resolveStepLabel(step.label, t)).join(', ')}</span>
        {remaining > 0 && <span> {t('sagaProgress.activity.more', { count: remaining })}</span>}
        {retryingStep && (
          <span className="saga-progress__retrying">
            {' '}
            {t('sagaProgress.activity.retrying', { attempt: retryingStep.retryCount + 1 })}
          </span>
        )}
      </>
    );
  }

  return (
    <div className="saga-progress__activity" aria-live="polite" data-testid="saga-progress-activity">
      {body}
    </div>
  );
};

interface StepListProps {
  progress: SagaProgress;
  t: Translate;
}

/**
 * Collapsed by default: a foundation deployment is 40–60 steps and the counts above already
 * answer "how far along". Native `<details>`/`<summary>` so Enter/Space and the announcement of
 * expanded state come from the platform.
 */
const StepList: React.FC<StepListProps> = ({ progress, t }) => (
  <details className="saga-progress__step-list" data-testid="saga-progress-step-list">
    <summary className="saga-progress__step-list-summary">
      {t('sagaProgress.steps.disclosure', { completed: progress.counts.completed, total: progress.counts.total })}
    </summary>
    <StepRows rows={progress.steps.map((step) => ({ step, status: badgeStatusOf(step) }))} t={t} />
  </details>
);

interface FetchErrorNoticeProps {
  t: Translate;
  onRetry?: () => void;
}

/**
 * Shown *beside* a snapshot we already have: the hook deliberately keeps the last good
 * `progress` across a failed poll, so one transient failure must not replace the working view.
 */
const FetchErrorNotice: React.FC<FetchErrorNoticeProps> = ({ t, onRetry }) => (
  <div className="saga-progress__stale-notice" data-testid="saga-progress-stale-notice">
    <output>{t('sagaProgress.error.stale')}</output>
    {onRetry && <ButtonAtom label={t('sagaProgress.error.retry')} variant="text" onClick={onRetry} />}
  </div>
);

interface OutcomeViewProps {
  progress: SagaProgress;
  t: Translate;
  className: string;
}

const CompletedView: React.FC<OutcomeViewProps> = ({ progress, t, className }) => {
  const outcome = progress.outcome;
  const summary = outcome?.kind === 'completed' ? outcome.summary : null;
  const durationMs = progress.completedAt
    ? Math.max(0, new Date(progress.completedAt).getTime() - new Date(progress.startedAt).getTime())
    : Math.max(0, progress.elapsedMs);

  return (
    <div className={`${className} saga-progress--completed`} data-testid="saga-progress-completed">
      <StepStatusBadge status="completed" t={t} />
      {summary ? (
        <output className="saga-progress__summary">
          {t('sagaProgress.outcome.completed.summary', {
            spheres: t('sagaProgress.outcome.completed.spheres', { count: summary.spheresCreated }),
            subApproaches: t('sagaProgress.outcome.completed.subApproaches', { count: summary.subApproaches }),
            duration: formatShortDuration(durationMs, t),
          })}
        </output>
      ) : (
        <output className="saga-progress__announcement">{t(STATUS_WORD_KEY.completed)}</output>
      )}
    </div>
  );
};

const FailedOrCompensatedView: React.FC<OutcomeViewProps & { variant: 'block' | 'inline' }> = ({
  progress,
  t,
  className,
  variant,
}) => {
  const outcome = progress.outcome;
  if (!outcome || outcome.kind === 'completed') return null;

  const failedStep = outcome.kind === 'failed' ? outcome.failedStep : null;
  const revertedIds = new Set(outcome.compensatedSteps.map((step) => step.id));
  const completedSteps = progress.steps.filter(
    (step: ProgressStep) => step.status === 'completed' && !revertedIds.has(step.id)
  );
  // The spec's "partially complete" row: what never started is part of the account of what
  // happened, and leaving it out reads as "everything except one step succeeded".
  const notStartedSteps = progress.steps.filter(
    (step: ProgressStep) =>
      (step.status === 'pending' || step.status === 'waiting') && step.id !== failedStep?.id && !revertedIds.has(step.id)
  );

  const rows: { step: ProgressStep; status: BadgeStatus }[] = [
    ...completedSteps.map((step) => ({ step, status: 'completed' as BadgeStatus })),
    ...(failedStep ? [{ step: failedStep, status: 'failed' as BadgeStatus }] : []),
    ...outcome.compensatedSteps.map((step) => ({ step, status: 'reverted' as BadgeStatus })),
    ...notStartedSteps.map((step) => ({ step, status: 'pending' as BadgeStatus })),
  ];

  // A run can fail without any step failing — the timeout sweeper exhausting the run's own
  // attempts, or an incomplete compensation. Then the reason is run-level, not on a row.
  const runLevelError = outcome.kind === 'failed' && !failedStep ? outcome.error : null;

  return (
    <div className={`${className} saga-progress--failed`} data-testid="saga-progress-failed">
      {runLevelError && <div className="saga-progress__error-message">{t(runLevelError.messageKey)}</div>}
      <StepRows rows={rows} t={t} />
      {outcome.compensatedSteps.length > 0 && (
        <div className="saga-progress__reverted-count">
          {t('sagaProgress.outcome.reverted', { count: outcome.compensatedSteps.length })}
        </div>
      )}
      {outcome.remainingSideEffects && (
        <div className="saga-progress__remaining-side-effects">{t('sagaProgress.outcome.failed.remainingSideEffects')}</div>
      )}
      <output className="saga-progress__what-now">
        {variant === 'inline'
          ? t('sagaProgress.outcome.failed.deployAgain')
          : t('sagaProgress.outcome.failed.whatNow')}
      </output>
      <div className="saga-progress__support-ref">{t('sagaProgress.outcome.supportRef', { runId: outcome.supportRef })}</div>
    </div>
  );
};

/**
 * Renders one `SagaProgress` snapshot (from `useSagaProgress`) as: the synthetic
 * pre-run phase, a loading skeleton, a fetch-error state, a live working/waiting bar
 * with activity line, elapsed/estimate and a collapsible step list, or a terminal
 * completed/failed/compensated outcome.
 */
const SagaProgressMolecule: React.FC<SagaProgressMoleculeProps> = ({
  progress,
  loading,
  error,
  deniedReason,
  onRetry,
  variant = 'block',
  surface,
  className = '',
}) => {
  const { t } = useTranslation();
  useSagaProgressAnalytics(progress, surface);
  const classes = ['saga-progress', `saga-progress--variant-${variant}`, className].filter(Boolean).join(' ').trim();

  if (deniedReason) {
    const copy =
      deniedReason === 'forbidden'
        ? { title: t('sagaProgress.denied.forbidden.title'), description: t('sagaProgress.denied.forbidden.description') }
        : { title: t('sagaProgress.denied.notFound.title'), description: t('sagaProgress.denied.notFound.description') };
    return (
      <div className={classes} data-testid="saga-progress-denied">
        <EmptyStateAtom icon="pi-lock" title={copy.title} description={copy.description} />
      </div>
    );
  }

  // `runId === null` is the discriminator, not `synthetic` truthiness: it is
  // what distinguishes `SyntheticSagaProgress` (no run exists yet) from a real
  // `SagaProgress`, and it is what narrows `progress` to `SagaProgress` for
  // every view below — all of which read run-level fields.
  if (progress?.runId === null) {
    const elapsedMs = Math.max(0, Date.now() - new Date(progress.synthetic.startedAt).getTime());
    return (
      <div className={classes} data-testid="saga-progress-synthetic">
        {/* A status line, not a heading: the host already names the wait (the sign-up screen's
            h1), and there is no subject to title yet — no run, so no approach or target. */}
        <output className="saga-progress__status-line">{t('sagaProgress.synthetic.heading')}</output>
        <ProgressBarAtom indeterminate ariaLabel={t('sagaProgress.synthetic.heading')} />
        <div className="saga-progress__elapsed">
          {t('sagaProgress.elapsed', { duration: formatShortDuration(elapsedMs, t) })}
        </div>
      </div>
    );
  }

  // A fetch failure only takes over the surface while there is nothing to show; once a snapshot
  // is in hand the hook keeps it, and the failure is a notice beside the working view.
  if (!progress) {
    if (error) {
      return (
        <div className={classes} data-testid="saga-progress-error">
          <EmptyStateAtom
            icon="pi-exclamation-triangle"
            title={t('sagaProgress.error.title')}
            description={t('sagaProgress.error.description')}
          />
          {onRetry && <ButtonAtom label={t('sagaProgress.error.retry')} variant="secondary" onClick={onRetry} />}
        </div>
      );
    }

    if (loading) {
      return (
        <div className={classes} data-testid="saga-progress-loading" aria-busy="true">
          <SkeletonAtom count={3} height="var(--spacing-3xl)" gap="var(--spacing-xs)" />
        </div>
      );
    }

    return null;
  }

  if (progress.status === 'failed' || progress.status === 'compensated') {
    return <FailedOrCompensatedView progress={progress} t={t} className={classes} variant={variant} />;
  }

  if (progress.status === 'completed') {
    return <CompletedView progress={progress} t={t} className={classes} />;
  }

  if (progress.status === 'compensating') {
    return (
      <div className={classes} data-testid="saga-progress-compensating">
        <ProgressBarAtom indeterminate tone="warning" ariaLabel={t('sagaProgress.compensating.label')} />
        <output className="saga-progress__status-line">{t('sagaProgress.compensating.label')}</output>
      </div>
    );
  }

  // pending / running / waiting
  const { counts } = progress;
  const value = counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : undefined;
  const estimate = estimateText(progress.estimate, t);

  return (
    <div className={classes} data-testid="saga-progress-working" aria-busy="true">
      <h2 className="saga-progress__heading">{subjectHeading(progress, t)}</h2>
      <ProgressBarAtom
        value={value ?? 0}
        indeterminate={value === undefined}
        showValue={false}
        ariaLabel={t('sagaProgress.working.ariaLabel')}
      />
      <ActivityLine progress={progress} t={t} />
      <div className="saga-progress__meta">
        <span>{t('sagaProgress.counts', { completed: counts.completed, total: counts.total })}</span>
        <span className="saga-progress__elapsed">
          {t('sagaProgress.elapsed', { duration: formatShortDuration(progress.elapsedMs, t) })}
        </span>
        {/* No estimate at `confidence: 'none'` — elapsed alone is the honest report. */}
        {estimate && <span className="saga-progress__estimate">{estimate}</span>}
      </div>
      {progress.steps.length > 0 && <StepList progress={progress} t={t} />}
      {error && <FetchErrorNotice t={t} onRetry={onRetry} />}
    </div>
  );
};

export default SagaProgressMolecule;
